import React, { useEffect, useState, useCallback, useRef } from 'react';
import { jsClientDeviceIdentifier } from 'header-scripts';
import { WithTranslationsProps } from 'react-utilities';
import { Loading } from 'react-style-guide';
import { eventStreamService } from 'core-roblox-utilities';
import { TOmniRecommendationSort, TGameData } from '../common/types/bedev1Types';
import bedev1Services from '../common/services/bedev1Services';
import eventStreamConstants, { PageContext } from '../common/constants/eventStreamConstants';
import { common } from '../common/constants/configConstants';
import { GameGrid } from '../common/components/GameGrid';
import { IEGameGrid } from '../common/components/IEGameGrid';
import { TOnTileClick } from '../common/components/GameTileUtils';
import { buildGameDetailUrl } from '../common/utils/browserUtils';

type TSortDetailGridProps = {
  sort: TOmniRecommendationSort;
  translate: WithTranslationsProps['translate'];
};

export const SortDetailGrid = ({ sort, translate }: TSortDetailGridProps): JSX.Element => {
  const gridRef = useRef<HTMLDivElement>(null);
  const tileRef = useRef<HTMLDivElement>(null);
  const [gameData, setGameData] = useState<TGameData[] | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  const hasMoreRows = useCallback(() => (gameData || []).length < sort.RecommendationList.length, [
    gameData,
    sort
  ]);

  const loadData = useCallback(async () => {
    setLoading(true);
    const numberOfLoadedGameData = (gameData || []).length;
    const recommendationsToLoad = sort.RecommendationList.slice(
      numberOfLoadedGameData,
      numberOfLoadedGameData + common.numberOfGameTilesPerLoad
    );
    try {
      const { ContentMetadata } = await bedev1Services.getOmniRecommendationsMetadata(
        recommendationsToLoad
      );
      setGameData([
        ...(gameData || []),
        ...recommendationsToLoad.map(data => ContentMetadata[data.ContentType][data.ContentId])
      ]);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
    setLoading(false);
  }, [gameData, sort]);

  const onScroll = useCallback(() => {
    const gridOffset = (gridRef.current?.offsetTop || 0) + (gridRef.current?.clientHeight || 0);
    const windowOffset = window.pageYOffset + window.innerHeight;
    if (windowOffset > gridOffset && !hasMoreRows()) {
      window.removeEventListener('scroll', onScroll);
    } else if (windowOffset > gridOffset && hasMoreRows() && loading === false) {
      // eslint-disable-next-line no-void
      void loadData();
    }
  }, [hasMoreRows, loadData, loading]);

  const onTileClick = useCallback<TOnTileClick>(
    (data, id) => {
      const gameDetailReferralParams = eventStreamConstants.gameDetailReferral({
        pid: data.placeId,
        ad: data.isSponsored,
        pos: id,
        gameSetTargetId: sort.TopicId,
        pg: PageContext.SortDetailPage
      });
      eventStreamService.sendEvent(...gameDetailReferralParams);
      window.open(buildGameDetailUrl(data.placeId, data.name), '_self');
    },
    [gameData?.length]
  );

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [onScroll, sort]);

  useEffect(() => {
    setGameData(undefined);
    // eslint-disable-next-line no-void
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  return (
    <React.Fragment>
      {jsClientDeviceIdentifier.isIE ? (
        <IEGameGrid
          ref={gridRef}
          tileRef={tileRef}
          gameData={gameData || []}
          translate={translate}
          onTileClick={onTileClick}
        />
      ) : (
        <GameGrid
          ref={gridRef}
          tileRef={tileRef}
          gameData={gameData || []}
          translate={translate}
          onTileClick={onTileClick}
        />
      )}
      {loading && <Loading />}
    </React.Fragment>
  );
};

export default SortDetailGrid;
